import {
  NarrativeCardSynthetic,
  NarrativeCardPopulated,
} from '@/components/NarrativeCard';

import { FragmentOf, gql, readFragment } from '@/utilities/gql';
import { NARRATIVE_CARD_FRAGMENT } from '@/components/NarrativeCard/narrativeCardFragment';
import { NarrativeSetFairValue } from './components/NarrativeSetFairValue';
import { cn } from '@simplywallst/ui-core/next';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import useUser from '@/hooks/useUser';
import { useSelector } from 'react-redux';
import { getLocationPathname } from '@/redux/location/selectors';
import { AriaRole } from 'react';
import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { Button } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@/utilities/redux-first-router.export';

export const NARRATIVE_CARD_COMPANY_FRAGMENT = gql(`
  fragment NarrativeCardOptionCompanyFields on Company @_unmask {
    id
    canonicalURL
    uniqueSymbolSlug
    country {
      iso2
    }
    primaryIndustry {
      slug
    }
    data {
      currencyInfo
    }
    analysisValue {
      priceTargetAnalystCount
    }

  }
`);

export const NARRATIVE_CARD_OPTION_FRAGMENT = gql(
  `
  fragment NarrativeCardOptionValuationOptionFields on ValuationOption @_unmask {
    type
    fairValue
    intrinsicDiscount

    narrative {
      id
      title
      isPrimary
      author {
        id
      }
      ...NarrativeCardFields
    }
  }
`,
  [NARRATIVE_CARD_FRAGMENT]
);

type Props = {
  valuationOption: FragmentOf<typeof NARRATIVE_CARD_OPTION_FRAGMENT>;
  company: FragmentOf<typeof NARRATIVE_CARD_COMPANY_FRAGMENT>;
  role?: AriaRole;
  onError: () => void;
};

const isSyntheticNarrative = (type: string): type is 'DCF' | 'ANALYSTS' =>
  ['DCF', 'ANALYSTS'].includes(type);

export const NarrativeCardOption = ({
  valuationOption,
  company,
  role,
  onError,
}: Props) => {
  const { data: user } = useUser();
  const { trackEvent } = useTracking();
  const pathname = useSelector(getLocationPathname);
  const valuationOptionUnmasked = readFragment(
    NARRATIVE_CARD_OPTION_FRAGMENT,
    valuationOption
  );
  const { narrative, type, fairValue, intrinsicDiscount } =
    valuationOptionUnmasked;

  if (!narrative) {
    console.error('Narrative is missing from valuation option');
    return null;
  }

  const { id, isPrimary, author } = narrative ?? {};

  const isMyNarrative = user.id === author.id;

  return (
    <div role={role}>
      {isSyntheticNarrative(type) ? (
        <NarrativeCardSynthetic
          className="rounded-none min-[375px]:rounded-x1_5"
          title={narrative.title}
          fairValue={fairValue}
          intrinsicDiscount={intrinsicDiscount}
          currencyIso={
            getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX'
          }
          priceTargetAnalystCount={
            company.analysisValue?.priceTargetAnalystCount
          }
          type={type}
          linkTo={`${company.canonicalURL}/valuation`}
          narrativeId={id}
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'view',
              narrativeId: id,
              companyId: company?.id,
              valuationType: type,
            });
          }}
        >
          {company.id && (
            <NarrativeSetFairValue
              companyId={company.id}
              narrativeId={id}
              isPrimary={isPrimary}
              onError={onError}
              valuationType={type}
            />
          )}
        </NarrativeCardSynthetic>
      ) : (
        <NarrativeCardPopulated
          narrative={narrative}
          className="rounded-none min-[375px]:rounded-x1_5"
          onClick={() => {
            trackEvent({
              action: 'click',
              modifier: 'view',
              narrativeId: id,
              companyId: company?.id,
              valuationType: 'USER_DEFINED',
            });
          }}
        >
          <div
            className={cn(
              'grid gap-2',
              isMyNarrative ? 'grid-cols-2' : 'grid-cols-1',
              'max-[375px]:grid-cols-1'
            )}
          >
            {isMyNarrative && (
              <Button
                as={RouterLink}
                styleType="gentleLarge"
                to={{
                  type: ROUTE_PUBLIC_NARRATIVE_EDIT,
                  payload: {
                    id,
                    query: {
                      company: company?.id.toLowerCase(),
                      referrer: pathname,
                    },
                  },
                }}
                className="text-center"
                onClick={() => {
                  trackEvent({
                    action: 'click',
                    target: 'edit-narrative',
                    valuationType: 'user_defined',
                  });
                }}
              >
                <EditSolid className="!mr-1" />
                Edit Narrative
              </Button>
            )}
            <NarrativeSetFairValue
              companyId={company.id}
              narrativeId={id}
              isPrimary={isPrimary}
              onError={onError}
              valuationType="USER_DEFINED"
            />
          </div>
        </NarrativeCardPopulated>
      )}
    </div>
  );
};
